import TurndownService from 'turndown';

const td = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced', bulletListMarker: '-' });

// Match the editor's Markdown style: "- item" / "1. item" with a single space
// (Turndown's default is "-   item"), nested content indented by the marker
// width, and checkbox items (legacy TipTap task lists) as "- [ ] item".
td.addRule('listItem', {
  filter: 'li',
  replacement: (content, node) => {
    const li = node as HTMLElement;
    const parent = li.parentNode as HTMLElement;
    let marker = '-';
    if (parent.nodeName === 'OL') {
      const start = Number(parent.getAttribute('start') || 1);
      marker = `${start + Array.prototype.indexOf.call(parent.children, li)}.`;
    }
    // The item's own checkbox: a direct child, or inside a direct <label>
    // (TipTap). Children are walked by hand — `:scope` selectors are not
    // reliable in every DOM implementation Turndown runs on.
    const isCheckbox = (el: Element) => el.nodeName === 'INPUT' && el.getAttribute('type') === 'checkbox';
    let checkbox: Element | null = null;
    for (const child of Array.from(li.children)) {
      if (isCheckbox(child)) checkbox = child;
      else if (child.nodeName === 'LABEL') checkbox = Array.from(child.children).find(isCheckbox) ?? null;
      if (checkbox) break;
    }
    const dataChecked = li.getAttribute('data-checked');
    const isTask = checkbox !== null || dataChecked !== null;
    if (isTask) {
      const checked = dataChecked === 'true' || checkbox?.hasAttribute('checked');
      marker += checked ? ' [x]' : ' [ ]';
    }
    const prefix = marker + ' ';
    // A <p> inside the item makes Turndown emit a loose list; task items
    // (whose text TipTap always wrapped in <p>) are kept tight.
    const isParagraph = !isTask && /\n$/.test(content);
    const body = content.replace(/^\n+|\n+$/g, '') + (isParagraph ? '\n' : '');
    // Nested content aligns with the item text; for "- [ ] " that is the "- ".
    const indent = ' '.repeat(isTask ? marker.length - 3 : prefix.length);
    return prefix + body.replace(/\n(?=[^\n])/g, '\n' + indent) + (li.nextSibling ? '\n' : '');
  },
});

// Decide whether a note is genuine HTML (produced by the legacy rich-text
// editor) or Markdown that merely contains "<" characters. The old heuristic
// matched any "<letter", which misfired on Markdown whose fenced code blocks
// legitimately contain HTML-looking text — e.g. `List<String>`, a shell
// redirect (`cat <file`), or an MCP command placeholder. Such notes were then
// fed through Turndown as if they were HTML, which escaped Markdown specials
// (# -> \#, _ -> \_, ` -> \`) and mangled the structure.
//
// Real editor HTML always contains a closing tag (</p>, </code>, …) or a void
// tag (<br>, <hr>, <img>); Markdown virtually never does. Code spans/blocks are
// stripped first so HTML shown verbatim *inside* code can't trigger a false
// positive.
function looksLikeHtml(s: string): boolean {
  const withoutCode = s
    .replace(/```[\s\S]*?```/g, '') // fenced code blocks
    .replace(/`[^`]*`/g, '');       // inline code spans
  return /<\/[a-z][a-z0-9]*\s*>|<(?:br|hr|img|input)\b[^>]*>/i.test(withoutCode);
}

export function htmlToMd(html: string): string {
  if (!html) return '';
  if (!looksLikeHtml(html)) return html;
  return td.turndown(html);
}

// Ensure a link has an absolute target. Markdown links without a scheme
// (e.g. "www.seznam.cz") would otherwise be treated as relative paths.
export function normalizeUrl(href?: string): string | undefined {
  if (!href) return href;
  const trimmed = href.trim();
  if (!trimmed) return trimmed;
  // Already has a scheme (http:, https:, mailto:, tel:, …), is an anchor,
  // a protocol-relative URL, or an absolute/relative path — leave as-is.
  if (
    /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('/')
  ) {
    return trimmed;
  }
  return `https://${trimmed}`;
}
